import { useAppSelector } from "integrations/redux/hooks"
import { useDrag, useDragLayer, useDrop } from "react-dnd"
import { useMoveMissionInGroups } from "./PilotTrackingMissionQueries"
import { selectRenamingGroupId } from "../redux/missions-table-selectors"

export const MISSION_TABLE_FLIGHT = "MISSION_TABLE_FLIGHT"

export type MissionDragItem = {
    missionId: string
    currentGroupId: string | null
    date: string
    droneName: string | null
}

export function useMissionDrag(
    missionId: string,
    currentGroupId: string | null,
    date: string,
    droneName: string | null
) {
    const renamingGroupId = useAppSelector(selectRenamingGroupId)

    const [{ isDragging }, dragRef] = useDrag(
        () => ({
            type: MISSION_TABLE_FLIGHT,
            item: { missionId, currentGroupId, date, droneName } satisfies MissionDragItem,
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
            canDrag: renamingGroupId === null,
        }),
        [missionId, currentGroupId, date, droneName, renamingGroupId]
    )

    return { isDragging, dragRef }
}

export function useGroupDrop(groupId: string, enabled: boolean) {
    const { moveMission } = useMoveMissionInGroups()

    const [{ isOver }, dropRef] = useDrop(
        () => ({
            accept: MISSION_TABLE_FLIGHT,
            canDrop: enabled
                ? (item: MissionDragItem) => item.currentGroupId !== groupId
                : () => false,
            drop: (item: MissionDragItem) => {
                void moveMission({
                    missionId: item.missionId,
                    fromGroupId: item.currentGroupId,
                    toGroupId: groupId,
                })
            },
            collect: (monitor) => ({
                isOver: enabled && !!monitor.isOver() && !!monitor.canDrop(),
            }),
        }),
        [groupId, enabled, moveMission]
    )

    return { isOver, dropRef }
}

export function useUngroupDrop(enabled: boolean) {
    const { moveMission } = useMoveMissionInGroups()

    const [{ isOver }, dropRef] = useDrop(
        () => ({
            accept: MISSION_TABLE_FLIGHT,
            canDrop: enabled
                ? (item: MissionDragItem) => item.currentGroupId !== null
                : () => false,
            drop: (item: MissionDragItem) => {
                void moveMission({
                    missionId: item.missionId,
                    fromGroupId: item.currentGroupId,
                    toGroupId: null,
                })
            },
            collect: (monitor) => ({
                isOver: enabled && !!monitor.isOver() && !!monitor.canDrop(),
            }),
        }),
        [enabled, moveMission]
    )

    return { isOver, dropRef }
}

export function useAnyMissionIsDragging() {
    return useDragLayer(
        (monitor) => monitor.isDragging() && monitor.getItemType() === MISSION_TABLE_FLIGHT
    )
}
